import React, { useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import PinMap from "../map/PinMap";
import { toast } from "sonner";
import { MapPin, RotateCcw, Save, X, History, AlertTriangle, CheckCircle2, Plus, Trash2 } from "lucide-react";

const fmt = (n) => (n == null ? "—" : Number(n).toFixed(6));
const when = (iso) => { try { return new Date(iso).toLocaleString("it-IT"); } catch { return iso || "—"; } };

export default function MapAdminPanel() {
  const [comuni, setComuni] = useState([]);
  const [warnKm, setWarnKm] = useState(6);
  const [sel, setSel] = useState("");
  const [draft, setDraft] = useState(null);        // {lat, lng} in editing
  const [validation, setValidation] = useState(null);
  const [history, setHistory] = useState([]);
  const [saving, setSaving] = useState(false);
  const [adding, setAdding] = useState(false);
  const [nc, setNc] = useState({ name: "", lat: 45.225, lng: 7.628, active: true });

  const load = () => api.get("/ops/map/comuni").then(({ data }) => {
    setComuni(data.comuni || []); setWarnKm(data.warn_km || 6);
    if (!sel && data.comuni?.length) setSel(data.comuni[0].id);
  }).catch(() => toast.error("Impossibile caricare i comuni"));

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const current = useMemo(() => comuni.find((c) => c.id === sel) || null, [comuni, sel]);

  const loadHistory = (cid) => api.get(`/ops/map/comuni/${cid}/history`).then(({ data }) => setHistory(data.history || [])).catch(() => setHistory([]));

  useEffect(() => {
    if (!current) return;
    setDraft({ lat: current.current.lat, lng: current.current.lng });
    setValidation(null);
    loadHistory(current.id);
  }, [sel]); // eslint-disable-line

  const dirty = draft && current && (draft.lat !== current.current.lat || draft.lng !== current.current.lng);

  const onPinChange = ({ lat, lng }) => {
    setDraft({ lat, lng });
    if (!current) return;
    api.post(`/ops/map/comuni/${current.id}/validate`, { lat, lng }).then(({ data }) => setValidation(data)).catch(() => setValidation(null));
  };

  const save = async () => {
    if (!current || !draft) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/ops/map/comuni/${current.id}`, draft);
      toast.success(`Posizione di ${current.name} salvata`);
      setValidation(data);
      await load(); await loadHistory(current.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Salvataggio non riuscito"); }
    finally { setSaving(false); }
  };

  const cancel = () => { if (current) { setDraft({ lat: current.current.lat, lng: current.current.lng }); setValidation(null); } };

  const rollback = async () => {
    if (!current) return;
    if (!window.confirm(`Ripristinare la posizione precedente di ${current.name}?`)) return;
    try {
      const { data } = await api.post(`/ops/map/comuni/${current.id}/rollback`);
      toast.success(data.to_base ? "Ripristinata la posizione originale" : "Posizione precedente ripristinata");
      await load(); await loadHistory(current.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Nessuna modifica da annullare"); }
  };

  const addCity = async () => {
    if (!nc.name.trim()) return toast.error("Inserisci il nome del comune");
    try {
      const { data } = await api.post("/ops/map/comuni/add", nc);
      toast.success(`${data.name} aggiunto alla mappa`);
      setAdding(false); setNc({ name: "", lat: 45.225, lng: 7.628, active: true });
      await load(); setSel(data.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Impossibile aggiungere il comune"); }
  };

  const deleteCity = async () => {
    if (!current?.custom) return;
    if (!window.confirm(`Eliminare il comune aggiunto "${current.name}"? L'operazione è definitiva.`)) return;
    try {
      await api.delete(`/ops/map/comuni/added/${current.id}`);
      toast.success("Comune eliminato");
      setSel(""); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  return (
    <div className="space-y-5" data-testid="map-admin-panel">
      <div>
        <h2 className="font-display font-bold text-xl flex items-center gap-2"><MapPin className="h-5 w-5 text-[#00E676]" /> Gestisci Mappa — Centri Comuni</h2>
        <p className="text-sm text-zinc-500">Riposiziona il centro amministrativo usato da Fuma (apertura comune, Energia Fuma, centering, aggregazioni). Non modifica le label native della mappa.</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <div className="space-y-4">
          <div className="rounded-2xl border border-white/10 bg-[#1C1C1F] p-3">
            {!adding ? (
              <button data-testid="map-admin-add-toggle" onClick={() => setAdding(true)}
                className="flex items-center gap-1.5 text-sm text-[#00E676] font-semibold"><Plus className="h-4 w-4" /> Aggiungi città</button>
            ) : (
              <div className="space-y-3" data-testid="map-admin-add-form">
                <div className="flex items-center justify-between">
                  <div className="font-semibold text-sm">Nuovo comune</div>
                  <button onClick={() => setAdding(false)} className="text-zinc-500"><X className="h-4 w-4" /></button>
                </div>
                <input data-testid="map-admin-add-name" value={nc.name} placeholder="Nome comune (es. Villanova Canavese)"
                  onChange={(e) => setNc((n) => ({ ...n, name: e.target.value }))}
                  className="w-full bg-[#0F1016] border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm" />
                <PinMap lat={nc.lat} lng={nc.lng} onChange={({ lat, lng }) => setNc((n) => ({ ...n, lat, lng }))} height={220} />
                <div className="flex items-center justify-between text-xs">
                  <span className="font-mono text-zinc-400">{fmt(nc.lat)}, {fmt(nc.lng)}</span>
                  <label className="flex items-center gap-1.5 text-zinc-400"><input type="checkbox" checked={nc.active} onChange={(e) => setNc((n) => ({ ...n, active: e.target.checked }))} /> Attivo</label>
                </div>
                <button data-testid="map-admin-add-save" onClick={addCity}
                  className="w-full flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full bg-[#00E676] text-black font-semibold text-sm"><Plus className="h-4 w-4" /> Aggiungi alla mappa</button>
              </div>
            )}
          </div>

          <div>
            <label className="text-xs text-zinc-500 uppercase tracking-widest">Comune</label>
            <div className="flex gap-2 mt-1.5">
              <select data-testid="map-admin-select" value={sel} onChange={(e) => setSel(e.target.value)}
                className="flex-1 bg-[#1C1C1F] border border-white/10 rounded-xl px-3 py-3 text-white">
                {comuni.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}{c.custom ? " • aggiunto" : ""}{c.overridden ? " • spostato" : ""}{c.active ? "" : " (bloccato)"}</option>
                ))}
              </select>
              {current?.custom && (
                <button data-testid="map-admin-delete-city" onClick={deleteCity} title="Elimina comune aggiunto"
                  className="px-3 rounded-xl border border-red-500/40 text-red-400 hover:bg-red-500/10"><Trash2 className="h-4 w-4" /></button>
              )}
            </div>
          </div>

          {current && (
            <>
              <PinMap lat={draft?.lat} lng={draft?.lng} onChange={onPinChange} height={280} />

              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-xl bg-[#1C1C1F] border border-white/10 p-3" data-testid="map-admin-current-coords">
                  <div className="text-[11px] text-zinc-500">Nuova posizione</div>
                  <div className="text-sm font-mono">{fmt(draft?.lat)}, {fmt(draft?.lng)}</div>
                </div>
                <div className="rounded-xl bg-[#1C1C1F] border border-white/10 p-3">
                  <div className="text-[11px] text-zinc-500">Posizione salvata</div>
                  <div className="text-sm font-mono">{fmt(current.current.lat)}, {fmt(current.current.lng)}</div>
                </div>
              </div>

              {validation?.warning ? (
                <div className="rounded-xl p-3 border border-amber-500/40 bg-amber-500/10 flex gap-2 text-sm" data-testid="map-admin-warning">
                  <AlertTriangle className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" /><span>{validation.warning}</span>
                </div>
              ) : validation ? (
                <div className="rounded-xl p-3 border border-[#00E676]/30 bg-[#00E676]/10 flex gap-2 text-sm" data-testid="map-admin-ok">
                  <CheckCircle2 className="h-4 w-4 text-[#00E676] shrink-0 mt-0.5" /><span>Posizione coerente con {current.name} ({validation.distance_from_base_km} km dal centro base).</span>
                </div>
              ) : null}

              <div className="flex flex-wrap gap-2">
                <button data-testid="map-admin-save" disabled={!dirty || saving} onClick={save}
                  className="flex items-center gap-1.5 px-4 py-3 rounded-full bg-[#00E676] text-black font-semibold disabled:opacity-40">
                  <Save className="h-4 w-4" /> {saving ? "Salvataggio…" : "Salva posizione"}
                </button>
                <button data-testid="map-admin-cancel" disabled={!dirty} onClick={cancel}
                  className="flex items-center gap-1.5 px-4 py-3 rounded-full border border-white/15 text-sm disabled:opacity-40">
                  <X className="h-4 w-4" /> Annulla
                </button>
                <button data-testid="map-admin-rollback" disabled={!current.overridden} onClick={rollback}
                  className="flex items-center gap-1.5 px-4 py-3 rounded-full border border-white/15 text-sm disabled:opacity-40">
                  <RotateCcw className="h-4 w-4" /> Ripristina precedente
                </button>
              </div>
              <div className="text-[11px] text-zinc-500">Centro originale (base): {fmt(current.base.lat)}, {fmt(current.base.lng)}</div>
            </>
          )}
        </div>

        <div>
          <div className="text-xs uppercase tracking-widest text-zinc-500 flex items-center gap-1 mb-2"><History className="h-3.5 w-3.5" /> Storico modifiche</div>
          {history.length === 0 ? (
            <div className="text-zinc-600 text-sm p-4 rounded-xl bg-[#1C1C1F] border border-white/10" data-testid="map-admin-history-empty">Nessuno spostamento registrato per questo comune.</div>
          ) : (
            <ul className="space-y-2" data-testid="map-admin-history">
              {history.map((h, i) => (
                <li key={i} className="rounded-xl bg-[#1C1C1F] border border-white/10 p-3 text-sm">
                  <div className="flex items-center justify-between">
                    <span className={`font-semibold ${h.action === "rollback" ? "text-amber-400" : "text-[#00E676]"}`}>
                      {h.action === "rollback" ? "↩ Ripristino" : "→ Spostamento"}
                    </span>
                    <span className="text-[11px] text-zinc-500">{when(h.at)}</span>
                  </div>
                  <div className="font-mono text-xs text-zinc-400 mt-1">{fmt(h.prev_lat)}, {fmt(h.prev_lng)} → {fmt(h.lat)}, {fmt(h.lng)}</div>
                  <div className="text-[11px] text-zinc-500 mt-1">da {h.by_name || h.by || "—"} · {h.by_role || "?"}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
